import React from 'react';
import BlogPost from '../BlogPost';

const BlogFullFundingVsBaseline: React.FC = () => {
  return (
    <BlogPost
      title="Full Funding vs. Baseline Funding Under HB 913: Which Schedule Is Right for Your Florida Condo?"
      description="Florida's HB 913 introduced the 'baseline funding plan' as the minimum standard for SIRS reserves. Here's what it means, how it differs from full funding, and how to transition your association's approach."
      publishDate="May 2026"
      readTime="11 min read"
    >
      <p>
        When your engineering firm delivers your structural integrity reserve study,
        somewhere in the report you will see language about funding schedules. You
        may see terms like "full funding," "threshold funding," "baseline funding,"
        or simply "recommended contributions." These are not interchangeable. Under
        Florida's HB 913, there is only one that satisfies the state's minimum legal
        standard for your association's reserve plan.
      </p>
      <p>
        This guide explains the difference between the three common reserve funding
        models, what HB 913 specifically requires, and how your board can transition
        from a spreadsheet-based approach to a compliant, software-generated
        baseline funding plan.
      </p>

      <h2>The Three Reserve Funding Models Explained</h2>
      <p>
        Before HB 913, Florida condominium boards had significant discretion in how
        they approached reserve funding — including the ability to waive reserve
        contributions entirely with a unit owner vote. That discretion is now gone
        for SIRS reserves. But understanding the three standard models helps
        explain how your existing reserve study may differ from what HB 913 now requires.
      </p>

      <h3>Model 1: Full Funding</h3>
      <p>
        Full funding means maintaining reserve balances equal to the percentage of each
        component's useful life that has been consumed. If a roof has a 20-year useful
        life and is 10 years old, full funding means holding 50% of the replacement cost
        in reserves today.
      </p>
      <p>
        Full funding is the most conservative approach. It provides the largest cushion
        against unexpected cost increases, inflation surprises, and scope changes. It also
        produces the highest annual contributions — often significantly higher than what
        boards have historically been contributing.
      </p>
      <p>
        Full funding is generally recommended by the reserve study industry as best
        practice. It is not, however, what HB 913 mandates as the minimum standard.
      </p>

      <h3>Model 2: Threshold Funding</h3>
      <p>
        Threshold funding sets a minimum reserve balance floor (the "threshold") and
        requires contributions sufficient to keep the balance above that floor at all
        times. The threshold is typically set at a percentage of fully funded balance —
        commonly 25%, 50%, or 70% — or at a specific dollar amount.
      </p>
      <p>
        Threshold funding allows boards to set a lower contribution rate than full
        funding while still maintaining some buffer against the reserve balance
        reaching zero. It was a common approach in Florida before HB 913 for associations
        that wanted to limit assessment increases while still maintaining a responsible
        reserve posture.
      </p>
      <p>
        HB 913 does not use the term "threshold funding" and does not adopt this model
        as the statutory standard for SIRS reserves.
      </p>

      <h3>Model 3: Baseline Funding — The HB 913 Standard</h3>
      <p>
        Baseline funding, as defined under HB 913, requires that reserve contributions
        be set at a level sufficient to ensure the reserve fund balance never projects
        below zero at any point in a 30-year forward-looking analysis.
      </p>
      <p>
        This is a different mathematical objective than full funding or threshold funding.
        It does not require maintaining a specific percentage of fully funded balance.
        It does not require holding a reserve equal to consumed useful life. It requires
        one thing only: that the projected balance never hits zero over the next 30 years.
      </p>
      <p>
        Baseline funding is the minimum standard. It is also, in the context of Florida's
        history of reserve waivers and underfunding, a significant improvement over
        what many associations have been doing.
      </p>

      <h2>How Baseline and Full Funding Differ in Practice</h2>
      <p>
        The practical difference between baseline and full funding depends on your
        association's specific situation — particularly your current reserve balance
        and your component replacement schedule.
      </p>

      <h3>Scenario A: An Association With a Low Current Reserve Balance</h3>
      <p>
        Consider an association with a current reserve balance of $200,000 and three
        major replacement events projected in the next 12 years: roof at year 4
        ($500,000), concrete restoration at year 7 ($900,000), and window replacement
        at year 11 ($650,000).
      </p>
      <p>
        Under <strong>full funding</strong>, contributions would need to be set at a level
        that builds the balance toward the fully funded target for each component
        simultaneously. This is the most expensive path — contributions might be $150,000+
        per year.
      </p>
      <p>
        Under <strong>baseline funding</strong>, contributions are set at the minimum level
        required to ensure the balance never hits zero. Given the early large expenditure
        (roof at year 4), baseline contributions would still need to be substantial —
        but only as high as needed to avoid the zero-balance constraint. The calculation
        might produce contributions of $90,000–$110,000 per year, depending on current
        balance and the exact timing of each event.
      </p>
      <p>
        In this scenario, baseline funding produces a lower initial annual contribution.
        It also produces a lower reserve buffer — meaning there is less room for cost
        overruns without violating the baseline constraint.
      </p>

      <h3>Scenario B: An Association With a Healthy Current Reserve Balance</h3>
      <p>
        For an association that has been fully funding reserves consistently, the
        difference between baseline and full funding in terms of required annual
        contributions may be minimal — or baseline funding may even allow a slight
        reduction in contributions.
      </p>
      <p>
        In this scenario, the primary effect of HB 913 is to formalise the required
        documentation (the written baseline funding plan), ensure SIRS-specific reserves
        are tracked separately from other reserves, and mandate DBPR reporting.
        The financial impact is smaller than for underfunded associations.
      </p>

      <h2>What HB 913 Actually Requires You to Produce</h2>
      <p>
        Under Florida Statute §718.112(2)(g), your baseline funding plan must be:
      </p>
      <ol style={{lineHeight: '2rem'}}>
        <li>
          <strong>Based on all 8 mandated SIRS components</strong> with their
          engineering-assessed replacement costs and remaining useful life estimates
        </li>
        <li>
          <strong>A 30-year forward projection</strong> showing reserve fund balances
          for each year of the projection period
        </li>
        <li>
          <strong>Inflation-adjusted</strong> — the projection must account for cost
          inflation over the 30-year period
        </li>
        <li>
          <strong>Baseline-compliant</strong> — the reserve balance must never project
          below zero in any year of the 30-year period
        </li>
        <li>
          <strong>Adopted by the board</strong> via a formal resolution
        </li>
        <li>
          <strong>Submitted to DBPR</strong> via the online portal within 45 days of
          SIRS completion
        </li>
        <li>
          <strong>Communicated to unit owners</strong> with notification of the adopted
          funding schedule and its impact on assessments
        </li>
        <li>
          <strong>Tracked separately</strong> from operating funds and non-SIRS reserve
          accounts — SIRS reserves cannot be commingled, waived, or redirected
        </li>
      </ol>

      <h2>The Pooling vs. Component Method Question</h2>
      <p>
        One question that frequently arises when boards are generating their baseline
        funding plan is whether to use component-based accounting or pooled reserve
        accounting.
      </p>
      <p>
        Under <strong>component accounting</strong>, each SIRS item has its own
        sub-account or tracked balance. Contributions are allocated to each component
        separately, and replacement costs are drawn from the specific component's balance.
      </p>
      <p>
        Under <strong>pooled accounting</strong>, all SIRS reserve contributions go into
        a single account, and replacement costs are drawn from the pool regardless of
        which component is being replaced. The baseline constraint applies to the
        total pool balance.
      </p>
      <p>
        HB 913 requires that SIRS reserves be tracked separately from operating funds
        and non-SIRS reserves, but does not mandate component-level sub-accounts within
        the SIRS reserve itself. Many Florida associations use pooled accounting for
        their SIRS reserves, with the baseline funding plan serving as the projection
        tool that validates the pool's sufficiency.
      </p>
      <p>
        Consult your association's accountant or legal counsel regarding the accounting
        method that is most appropriate for your specific situation.
      </p>

      <h2>Transitioning From a Spreadsheet to a Compliant Plan</h2>
      <p>
        Many associations have been managing reserve projections in Excel spreadsheets —
        sometimes for decades. If your association is in this situation, here is how
        to transition to an HB 913-compliant baseline funding plan:
      </p>

      <h3>Step 1: Get Your SIRS Engineering Data Right</h3>
      <p>
        Your spreadsheet may have been based on component estimates from a prior reserve
        study, a contractor quote, or a board-generated estimate. For HB 913 compliance,
        the component data must come from a SIRS report prepared by a licensed architect,
        engineer, or reserve specialist. You cannot self-certify the structural component
        assessments.
      </p>
      <p>
        If your association does not yet have a completed SIRS, that is the required
        first step. The engineering firm's report is the input that drives everything else.
      </p>

      <h3>Step 2: Extract the Three Critical Numbers for Each Component</h3>
      <p>
        For each of the 8 mandated components, you need: (1) replacement cost, (2) remaining
        useful life in years, and (3) current reserve balance allocated to that component
        (or your total reserve balance if using pooled accounting). These three inputs drive
        the entire baseline funding calculation.
      </p>

      <h3>Step 3: Run the 30-Year Projection</h3>
      <p>
        The projection must calculate, for each future year: the opening reserve balance,
        plus annual contributions, minus any replacement expenditures in that year, equals
        the closing balance. Apply an inflation adjustment to each component's replacement
        cost based on its remaining useful life. The closing balance in every single year
        must be zero or positive.
      </p>
      <p>
        This is the calculation where manual spreadsheets create the most risk. Inflation
        compounding over 30 years, multiple overlapping replacement events, and the
        baseline constraint interacting with early large expenditures all create
        opportunities for spreadsheet errors that are not always obvious. A small
        formula error in year 1 can compound into a material miscalculation by year 15.
      </p>

      <h3>Step 4: Document Everything and Submit</h3>
      <p>
        Your baseline funding plan should clearly show all inputs (component list,
        costs, useful lives, starting balance, contribution rate, inflation assumption),
        all outputs (year-by-year balance projections), and the board resolution adopting
        the plan. This package is submitted to DBPR via the online portal.
      </p>
      <p>
        Keep a complete record of the adopted plan. HB 913 requires SIRS records to be
        retained for at least 15 years. When your SIRS is updated — which is required
        periodically under the statute — your prior funding plans become part of the
        compliance record demonstrating consistent board stewardship.
      </p>

      <h2>Why "Minimum Baseline" Is Not Necessarily the Right Target</h2>
      <p>
        The baseline funding standard in HB 913 is a legal minimum. It is not necessarily
        the optimal reserve funding strategy for your association.
      </p>
      <p>
        A plan that barely keeps the reserve balance above zero provides no buffer for:
        cost overruns on major projects (which are common in South Florida's construction
        market), accelerated component degradation (which happens in coastal environments),
        or unexpected emergency expenditures between major replacement cycles.
      </p>
      <p>
        Boards that adopt a contribution rate at the absolute baseline minimum are making
        a choice that optimises for short-term owner assessment costs at the expense of
        long-term financial resilience. That trade-off is legitimate — owners have a
        genuine interest in minimising assessments — but the board should make it
        explicitly and document that it was a considered decision, not an oversight.
      </p>
      <p>
        The right target for most associations is a contribution rate that keeps the
        projected balance meaningfully above the zero floor — providing a cushion that
        absorbs reasonable cost uncertainty without requiring a special assessment the
        moment a project runs over budget.
      </p>

      <h2>Funding Models Under HB 913: Quick Reference</h2>

      <table style={{width: '100%', borderCollapse: 'collapse', marginBottom: '1.5rem', fontSize: '0.9rem'}}>
        <thead>
          <tr style={{backgroundColor: '#1B3A5C', color: 'white'}}>
            <th style={{padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #2A9D8F'}}>Feature</th>
            <th style={{padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #2A9D8F'}}>Full Funding</th>
            <th style={{padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #2A9D8F'}}>Threshold Funding</th>
            <th style={{padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #2A9D8F'}}>Baseline (HB 913 Minimum)</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb'}}>
            <td style={{padding: '0.75rem'}}>HB 913 compliant?</td>
            <td style={{padding: '0.75rem', color: '#2A9D8F', fontWeight: 600}}>Yes</td>
            <td style={{padding: '0.75rem', color: '#f59e0b', fontWeight: 600}}>Depends on threshold</td>
            <td style={{padding: '0.75rem', color: '#2A9D8F', fontWeight: 600}}>Yes (minimum standard)</td>
          </tr>
          <tr style={{borderBottom: '1px solid #e5e7eb'}}>
            <td style={{padding: '0.75rem'}}>Annual contribution level</td>
            <td style={{padding: '0.75rem'}}>Highest</td>
            <td style={{padding: '0.75rem'}}>Medium</td>
            <td style={{padding: '0.75rem'}}>Lowest compliant level</td>
          </tr>
          <tr style={{borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb'}}>
            <td style={{padding: '0.75rem'}}>Reserve buffer built in?</td>
            <td style={{padding: '0.75rem'}}>Largest</td>
            <td style={{padding: '0.75rem'}}>Medium</td>
            <td style={{padding: '0.75rem'}}>Minimal (balance may approach zero)</td>
          </tr>
          <tr style={{borderBottom: '1px solid #e5e7eb'}}>
            <td style={{padding: '0.75rem'}}>Risk of special assessment</td>
            <td style={{padding: '0.75rem'}}>Lowest</td>
            <td style={{padding: '0.75rem'}}>Moderate</td>
            <td style={{padding: '0.75rem'}}>Higher if costs exceed projections</td>
          </tr>
          <tr style={{backgroundColor: '#f9fafb'}}>
            <td style={{padding: '0.75rem'}}>Best suited for</td>
            <td style={{padding: '0.75rem'}}>Financially strong associations, high-value buildings</td>
            <td style={{padding: '0.75rem'}}>Boards balancing owner cost sensitivity with prudence</td>
            <td style={{padding: '0.75rem'}}>Associations with cash flow constraints needing legal compliance</td>
          </tr>
        </tbody>
      </table>

      <h2>The Bottom Line</h2>
      <p>
        HB 913's baseline funding standard represents a genuine improvement over
        Florida's pre-2022 reserve culture — where waiver votes were common and
        "zero reserves" was a legal option. The requirement that reserve balances
        never project to zero over 30 years forces every board to confront the actual
        long-term cost of maintaining their building.
      </p>
      <p>
        Whether your association targets the legal minimum baseline or a more
        conservative full-funding approach, the starting point is the same: a
        completed SIRS report, accurate component data, and a 30-year projection
        that satisfies the statutory requirement. Get that documented, adopted,
        and submitted to DBPR — then have the conversation with your owners about
        where to target the reserve balance within the compliant range.
      </p>
    </BlogPost>
  );
};

export default BlogFullFundingVsBaseline;
